/**
 * Mapbox Directions client that hides the 25-coordinate limit entirely.
 *
 * Callers hand over any number of waypoints and get one route back. Internally
 * that may be several parallel requests whose geometries are stitched at shared
 * boundary waypoints.
 */

import { chunkWaypoints, expectedRequestCount, MAPBOX_MAX_COORDS_PER_REQUEST, stitchGeometries } from './chunk.js';
import { bboxOf } from './geo.js';
import { locatePointsAlongRoute } from './locate.js';
import { annotationToKph, encodeSpeedRuns, type MaxspeedAnnotation, type SpeedRun } from './speed.js';
import { decodePolyline, encodePolyline } from './polyline.js';
import type {
  BBox,
  LngLat,
  Maneuver,
  RouteLeg,
  RouteOptions,
  RoutingProfile,
  Waypoint,
} from './types.js';

export interface DirectionsResult {
  /** Encoded polyline6 of the whole route. */
  geometry: string;
  coordinates: LngLat[];
  distance: number;
  duration: number;
  legs: RouteLeg[];
  bbox: BBox;
  /** How many API calls this took. Surfaced so the planner can show it while routing. */
  requestCount: number;
  /**
   * Speed limit per geometry segment, run-length encoded. One shorter than the
   * coordinate count, matching the segments between them.
   */
  speedLimits: SpeedRun[];
  /** Every turn on the route, in order, placed by distance along it. */
  maneuvers: Maneuver[];
}

export class DirectionsError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'DirectionsError';
  }
}

export interface DirectionsClientOptions {
  accessToken: string;
  /** Injectable for tests and for the Worker, which has its own fetch. */
  fetchImpl?: typeof fetch;
  baseUrl?: string;
  maxCoordsPerRequest?: number;
}

function excludeParam(options: RouteOptions): string | null {
  const exclude: string[] = [];
  if (options.avoidTolls) exclude.push('toll');
  if (options.avoidFerries) exclude.push('ferry');
  if (options.avoidMotorways) exclude.push('motorway');
  return exclude.length > 0 ? exclude.join(',') : null;
}

interface RawManeuver {
  location: LngLat;
  type: string;
  modifier?: string;
  name?: string;
  instruction: string;
}

interface RawLeg {
  distance: number;
  duration: number;
  fromWaypointId: string;
  toWaypointId: string;
  /** Kind of the waypoint this leg starts at; used to undo chunk-boundary splits. */
  fromKind: Waypoint['kind'];
}

/**
 * Constrains which way a leg may leave a coordinate.
 *
 * Without this, the router assumes the driver can set off in any direction —
 * including straight back the way they came. That is fine for planning a route
 * on a map and completely wrong for getting someone back onto one: on a dual
 * carriageway, through a one-way system, or anywhere with a central
 * reservation, "turn around" is not an instruction that can be followed.
 */
export interface Bearing {
  /** Direction of travel, degrees clockwise from north. */
  angle: number;
  /** How far either side of it the route may depart. */
  tolerance: number;
}

export interface RouteExtras {
  signal?: AbortSignal;
  /**
   * One entry per waypoint, or null for no constraint on that waypoint.
   * Typically only the first is constrained — where the driver already is and
   * already pointing.
   */
  bearings?: Array<Bearing | null>;
  /**
   * Give up after this long, and throw a DirectionsError coded 'Timeout'.
   *
   * fetch has no timeout of its own, and a request that never answers is
   * worse than one that fails: callers hold off starting another until it
   * settles, so one hung request mid-drive stops the way back updating for as
   * long as it hangs. A timeout is not the caller's cancellation — their own
   * signal stays unaborted — so it reaches their ordinary failure path.
   */
  timeoutMs?: number;
}

/** A route as one request returns it, before chunks are stitched together. */
interface RawRoute {
  coordinates: LngLat[];
  distance: number;
  duration: number;
  legs: RawLeg[];
  speeds: Array<number | null>;
  maneuvers: RawManeuver[];
}

export class DirectionsClient {
  private readonly token: string;
  private readonly fetchImpl: typeof fetch;
  private readonly baseUrl: string;
  private readonly maxCoords: number;

  constructor(options: DirectionsClientOptions) {
    this.token = options.accessToken;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.baseUrl = options.baseUrl ?? 'https://api.mapbox.com/directions/v5/mapbox';
    this.maxCoords = options.maxCoordsPerRequest ?? MAPBOX_MAX_COORDS_PER_REQUEST;
  }

  buildUrl(
    chunk: Waypoint[],
    profile: RoutingProfile,
    options: RouteOptions,
    bearings?: Array<Bearing | null>,
    alternatives = false,
  ): string {
    const coords = chunk.map((w) => `${w.lng.toFixed(6)},${w.lat.toFixed(6)}`).join(';');
    const params = new URLSearchParams({
      access_token: this.token,
      geometries: 'polyline6',
      overview: 'full',
      // Turn instructions. Roughly doubles the response, which is worth it: the
      // alternative is a navigation app that cannot tell you about a turning.
      steps: 'true',
      // Fetched while planning so limits travel with the route: no runtime
      // quota, and they work with no signal.
      annotations: 'maxspeed',
      // The driving profile defaults this to true, which forbids reversing
      // direction at an intermediate coordinate. That is right when the
      // waypoints are places you are driving to; wrong for a route the user is
      // shaping by hand: drop a point on a road and then ask for somewhere
      // back the way you came, and the router cannot turn round, so it takes
      // the long way about instead. It also made chunk seams behave differently
      // from ordinary waypoints, since the constraint never applies to a
      // request's first or last coordinate.
      continue_straight: 'false',
    });

    // Mapbox's `waypoints` parameter marks which coordinates split the route into
    // legs; everything else merely shapes the line. That is exactly our
    // stop/via distinction. The first and last coordinate must always appear.
    const stopIndices = chunk
      .map((w, i) => (w.kind === 'stop' || i === 0 || i === chunk.length - 1 ? i : -1))
      .filter((i) => i !== -1);
    if (stopIndices.length < chunk.length) {
      params.set('waypoints', stopIndices.join(';'));
    }

    const exclude = excludeParam(options);
    if (exclude) params.set('exclude', exclude);
    if (alternatives) params.set('alternatives', 'true');

    if (bearings && bearings.some((b) => b !== null)) {
      // Mapbox wants an entry per coordinate; empty means unconstrained.
      params.set(
        'bearings',
        chunk
          .map((_, i) => {
            const bearing = bearings[i];
            return bearing ? `${Math.round(bearing.angle)},${Math.round(bearing.tolerance)}` : '';
          })
          .join(';'),
      );
    }

    return `${this.baseUrl}/${profile}/${coords}?${params.toString()}`;
  }

  async route(
    waypoints: Waypoint[],
    profile: RoutingProfile,
    options: RouteOptions,
    extras: RouteExtras = {},
  ): Promise<DirectionsResult> {
    if (waypoints.length < 2) {
      throw new DirectionsError('a route needs at least two waypoints', 'TooFewWaypoints');
    }

    const chunks = chunkWaypoints(waypoints, this.maxCoords);

    // One signal for every chunk, aborted by the caller's or by the timeout.
    const inner = new AbortController();
    const forward = () => inner.abort();
    extras.signal?.addEventListener('abort', forward);
    if (extras.signal?.aborted) inner.abort();
    let timedOut = false;
    const timer =
      extras.timeoutMs !== undefined
        ? setTimeout(() => {
            timedOut = true;
            inner.abort();
          }, extras.timeoutMs)
        : null;

    // Bearings are positional, so each chunk needs the slice matching its own
    // coordinates — and chunks overlap by one waypoint, exactly as the
    // coordinates do.
    let offset = 0;
    let responses: RawRoute[];
    try {
      responses = await Promise.all(
        chunks.map((chunk, index) => {
          const start = index === 0 ? 0 : offset;
          const slice = extras.bearings?.slice(start, start + chunk.length);
          offset = start + chunk.length - 1;
          return this.requestChunk(chunk, profile, options, inner.signal, slice).then((routes) => routes[0]!);
        }),
      );
    } catch (error) {
      if (timedOut) {
        throw new DirectionsError(`Mapbox Directions took longer than ${extras.timeoutMs} ms`, 'Timeout');
      }
      throw error;
    } finally {
      if (timer) clearTimeout(timer);
      extras.signal?.removeEventListener('abort', forward);
    }

    return assemble(responses, chunks.length);
  }

  /**
   * The best route between two points and whatever else Mapbox thinks worth
   * offering: up to three in all, fastest first.
   *
   * Two points only, and so always one request. Mapbox gives alternatives
   * for nothing more.
   */
  async alternatives(
    from: Waypoint,
    to: Waypoint,
    profile: RoutingProfile,
    options: RouteOptions,
    extras: RouteExtras = {},
  ): Promise<DirectionsResult[]> {
    const inner = new AbortController();
    const forward = () => inner.abort();
    extras.signal?.addEventListener('abort', forward);
    if (extras.signal?.aborted) inner.abort();
    let timedOut = false;
    const timer =
      extras.timeoutMs !== undefined
        ? setTimeout(() => {
            timedOut = true;
            inner.abort();
          }, extras.timeoutMs)
        : null;
    try {
      const routes = await this.requestChunk([from, to], profile, options, inner.signal, extras.bearings, true);
      return routes.map((route) => assemble([route], 1));
    } catch (error) {
      if (timedOut) {
        throw new DirectionsError(`Mapbox Directions took longer than ${extras.timeoutMs} ms`, 'Timeout');
      }
      throw error;
    } finally {
      if (timer) clearTimeout(timer);
      extras.signal?.removeEventListener('abort', forward);
    }
  }

  private async requestChunk(
    chunk: Waypoint[],
    profile: RoutingProfile,
    options: RouteOptions,
    signal?: AbortSignal,
    bearings?: Array<Bearing | null>,
    alternatives = false,
  ): Promise<RawRoute[]> {
    const url = this.buildUrl(chunk, profile, options, bearings, alternatives);
    const response = await this.fetchImpl(url, signal ? { signal } : {});

    if (!response.ok) {
      const body = (await response.text().catch(() => '')).slice(0, 400);
      throw new DirectionsError(
        `Mapbox Directions returned ${response.status}: ${body}`,
        'HttpError',
        response.status,
      );
    }

    const json = (await response.json()) as {
      code?: string;
      message?: string;
      routes?: ApiRoute[];
    };

    if (json.code && json.code !== 'Ok') {
      throw new DirectionsError(json.message ?? `Mapbox returned ${json.code}`, json.code);
    }

    if (!json.routes?.length) {
      throw new DirectionsError('Mapbox returned no route for this leg', 'NoRoute');
    }
    return json.routes.map((route) => parseRoute(route, chunk));
  }
}

type ApiRoute = {
  geometry: string;
  distance: number;
  duration: number;
  legs?: Array<{
    distance: number;
    duration: number;
    annotation?: { maxspeed?: MaxspeedAnnotation[] };
    steps?: Array<{
      name?: string;
      maneuver?: {
        location?: [number, number];
        type?: string;
        modifier?: string;
        instruction?: string;
      };
    }>;
  }>;
};

function parseRoute(route: ApiRoute, chunk: Waypoint[]): RawRoute {
  // Leg boundaries are the chunk's stops, always including its endpoints.
  const boundaries = chunk.filter((w, i) => w.kind === 'stop' || i === 0 || i === chunk.length - 1);
  const legs: RawLeg[] = (route.legs ?? []).map((leg, i) => ({
    distance: leg.distance,
    duration: leg.duration,
    fromWaypointId: boundaries[i]?.id ?? chunk[0]!.id,
    toWaypointId: boundaries[i + 1]?.id ?? chunk[chunk.length - 1]!.id,
    fromKind: boundaries[i]?.kind ?? 'stop',
  }));

  /**
   * Annotations arrive per leg, one entry per segment of that leg's geometry.
   * Concatenating the legs lines up with the chunk's own geometry, because a
   * chunk's coordinates are its legs joined at shared endpoints — so the
   * segment counts add up exactly.
   */
  const speeds: Array<number | null> = [];
  for (const leg of route.legs ?? []) {
    for (const entry of leg.annotation?.maxspeed ?? []) {
      speeds.push(annotationToKph(entry));
    }
  }

  /*
    The arrival step of one chunk and the departure step of the next describe
    the same moment at a seam that isn't a real stop, so both are dropped
    here and the whole-route arrival is added back by the caller's geometry —
    there is no instruction to give at a chunk boundary.
  */
  const maneuvers: RawManeuver[] = [];
  for (const leg of route.legs ?? []) {
    for (const step of leg.steps ?? []) {
      const m = step.maneuver;
      if (!m?.location || !m.type || !m.instruction) continue;
      if (m.type === 'depart' || m.type === 'arrive') continue;
      maneuvers.push({
        location: [m.location[0], m.location[1]],
        type: m.type,
        modifier: m.modifier,
        name: step.name || undefined,
        instruction: m.instruction,
      });
    }
  }

  return {
    coordinates: decodePolyline(route.geometry),
    distance: route.distance,
    duration: route.duration,
    legs,
    speeds,
    maneuvers,
  };
}

/** One route from the pieces its requests returned, in order. */
function assemble(responses: RawRoute[], requestCount: number): DirectionsResult {
  const coordinates = stitchGeometries(responses.map((r) => r.coordinates));
  const rawLegs = responses.flatMap((r) => r.legs);

  /*
    Turns are placed against the stitched geometry rather than by adding up
    per-chunk offsets. The arithmetic would work, but it has to stay in step
    with stitching for ever; measuring where each turn actually falls cannot
    drift out of agreement with the line the driver is following.
  */
  const rawManeuvers = responses.flatMap((r) => r.maneuvers);
  const at = locatePointsAlongRoute(
    coordinates,
    rawManeuvers.map((m) => m.location),
  );
  const maneuvers: Maneuver[] = rawManeuvers
    .map((m, i) => ({
      at: at[i] ?? 0,
      type: m.type,
      ...(m.modifier ? { modifier: m.modifier } : {}),
      ...(m.name ? { name: m.name } : {}),
      instruction: m.instruction,
    }))
    /*
      Sorted, because `nextManeuver` walks the array and takes the first turn
      ahead — an unsorted entry would be skipped silently.

      Measured placement puts two instructions at one junction a few metres
      either way of each other, which on a real route came out around 20 m
      out of order. Sorting is cheaper than making the projection exact, and
      makes the invariant true by construction rather than by argument.
    */
    .sort((a, b) => a.at - b.at);

  // Segment counts concatenate cleanly: stitching drops each chunk's
  // duplicated *coordinate*, never a segment, so no seam adjustment is needed.
  const speeds = responses.flatMap((r) => r.speeds);

  let distance = 0;
  let duration = 0;
  for (const r of responses) {
    distance += r.distance;
    duration += r.duration;
  }

  return {
    geometry: encodePolyline(coordinates),
    coordinates,
    distance,
    duration,
    legs: mergeLegsAcrossSeams(rawLegs),
    bbox: bboxOf(coordinates),
    requestCount,
    speedLimits: encodeSpeedRuns(speeds),
    maneuvers,
  };
}

/**
 * A chunk boundary can land on a `via` waypoint, which splits what should be one
 * leg into two. Rejoin any leg whose start waypoint isn't a real stop so the
 * itinerary shows stop-to-stop sections regardless of where chunks fell.
 */
export function mergeLegsAcrossSeams(rawLegs: RawLeg[]): RouteLeg[] {
  const merged: RouteLeg[] = [];
  for (const leg of rawLegs) {
    const previous = merged[merged.length - 1];
    if (previous && leg.fromKind !== 'stop' && previous.toWaypointId === leg.fromWaypointId) {
      previous.distance += leg.distance;
      previous.duration += leg.duration;
      previous.toWaypointId = leg.toWaypointId;
      continue;
    }
    merged.push({
      distance: leg.distance,
      duration: leg.duration,
      fromWaypointId: leg.fromWaypointId,
      toWaypointId: leg.toWaypointId,
    });
  }
  return merged;
}

export { expectedRequestCount };
